import { UserRankingDto } from '@repo/shared/dtos/analytics/UtilizationDto';
import { Trophy, Zap, Star, Moon, Clock } from 'lucide-react';
import { formatNumber } from '../../utils/dataUtils';
import { isChampion, isPowerMember } from '../utils/tierUtils';
import {
  TIER_DESC_CHAMPION,
  TIER_DESC_POWER,
  TIER_LABEL_CHAMPION,
  TIER_LABEL_POWER,
} from '../constants';

function LastSessionCell({ user }: { user: UserRankingDto }) {
  if (!user.last_session_date) {
    return <span className="text-sm text-[var(--anl-text-muted)]">No sessions</span>;
  }

  const lastSessionDate = new Date(user.last_session_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const sessionDate = new Date(lastSessionDate);
  sessionDate.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - sessionDate.getTime();
  const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  const formattedDate = lastSessionDate.toLocaleDateString();

  let label: string;
  if (days === 0) label = 'Today';
  else if (days === 1) label = 'Yesterday';
  else if (days > 1) label = `${days}d ago`;
  else label = 'Future';

  let colorClass: string;
  let Icon: React.ElementType | null = null;

  if (days <= 30) {
    colorClass = 'text-emerald-600 dark:text-emerald-400';
  } else if (days <= 60) {
    colorClass = 'text-orange-500 dark:text-orange-400';
    Icon = Clock;
  } else {
    colorClass = 'text-amber-600 dark:text-amber-400';
    Icon = Moon;
  }

  return (
    <div className={`flex flex-col items-center text-sm font-medium ${colorClass}`}>
      <div className="flex items-center gap-1">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        <span>{label}</span>
      </div>
      <span className="mt-0.5 text-xs font-normal text-[var(--anl-text-muted)]">
        {formattedDate}
      </span>
    </div>
  );
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <Trophy className="h-4 w-4 text-yellow-500" />;
  if (rank === 2)
    return (
      <span className="text-sm font-bold" style={{ color: 'var(--anl-text-muted)' }}>
        2
      </span>
    );
  if (rank === 3)
    return <span className="text-sm font-bold text-amber-600 dark:text-amber-400">3</span>;
  return (
    <span className="text-sm font-medium" style={{ color: 'var(--anl-text-muted)' }}>
      {rank}
    </span>
  );
}

function TierBadge({ user, isLumenaAdmin }: { user: UserRankingDto; isLumenaAdmin: boolean }) {
  if (!isLumenaAdmin) {
    return null;
  }

  if (isChampion(user)) {
    return (
      <span className="shrink-0" title={`${TIER_LABEL_CHAMPION} — ${TIER_DESC_CHAMPION}`}>
        <Star className="h-3.5 w-3.5 text-yellow-500" aria-label={TIER_LABEL_CHAMPION} />
      </span>
    );
  }
  if (isPowerMember(user)) {
    return (
      <span className="shrink-0" title={`${TIER_LABEL_POWER} — ${TIER_DESC_POWER}`}>
        <Zap className="h-3.5 w-3.5 text-emerald-500" aria-label={TIER_LABEL_POWER} />
      </span>
    );
  }
  return null;
}

interface MemberTableProps {
  data: UserRankingDto[];
  onUserClick?: (user: UserRankingDto) => void;
  startRank?: number;
  isLumenaAdmin?: boolean;
}

type MemberTableColumnKey = 'rank' | 'name' | 'sessions' | 'minutes' | 'mostUsed' | 'lastSession';

type MemberTableRowContext = {
  user: UserRankingDto;
  globalRank: number;
  isLumenaAdmin: boolean;
};

type MemberTableColumn = {
  key: MemberTableColumnKey;
  /** Width class applied to <col>. Empty string = no explicit width (auto). */
  widthClassName: string;
  /** Classes on <th> — must include visibility modifier if column is hidden on mobile. */
  headerClassName: string;
  /** Classes on <td> — must include visibility modifier if column is hidden on mobile. */
  cellClassName: string;
  header: string;
  renderCell: (context: MemberTableRowContext) => React.ReactNode;
};

const COLUMNS: MemberTableColumn[] = [
  {
    key: 'rank',
    widthClassName: 'w-10 sm:w-14',
    headerClassName:
      'px-2 py-3 sm:px-3 text-center text-xs font-medium uppercase tracking-wider text-[var(--anl-text-muted)]',
    cellClassName: 'px-2 py-3 sm:px-3 text-center',
    header: '#',
    renderCell: ({ globalRank }) => (
      <div className="mx-auto flex h-6 w-6 items-center justify-center">
        <RankBadge rank={globalRank} />
      </div>
    ),
  },
  {
    key: 'name',
    // On mobile: take remaining space. On sm+: fixed percentage.
    widthClassName: 'sm:w-[24%]',
    headerClassName:
      'px-2 py-3 sm:px-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--anl-text-muted)]',
    cellClassName: 'px-2 py-3 sm:px-3',
    header: 'Name',
    renderCell: ({ user, isLumenaAdmin }) => (
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-sm font-medium text-[var(--anl-text)]">
          {user.first_name} {user.last_name}
        </span>
        <TierBadge user={user} isLumenaAdmin={isLumenaAdmin} />
      </div>
    ),
  },
  {
    key: 'sessions',
    widthClassName: 'w-16 sm:w-[12%]',
    headerClassName:
      'px-2 py-3 sm:px-3 text-right text-xs font-medium uppercase tracking-wider text-[var(--anl-text-muted)]',
    cellClassName: 'px-2 py-3 sm:px-3 text-right text-sm font-medium text-[var(--anl-text)]',
    header: 'Sessions',
    renderCell: ({ user }) => formatNumber(user.total_sessions),
  },
  {
    key: 'minutes',
    // Hidden on mobile — lower priority than sessions and last session
    widthClassName: 'hidden sm:table-column sm:w-[13%]',
    headerClassName:
      'hidden sm:table-cell px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-[var(--anl-text-muted)]',
    cellClassName: 'hidden sm:table-cell px-3 py-3 text-right text-sm text-[var(--anl-text-muted)]',
    header: 'Minutes',
    renderCell: ({ user }) => formatNumber(Math.round(user.total_minutes)),
  },
  {
    key: 'mostUsed',
    // Hidden on mobile — text is too long and least critical at a glance
    widthClassName: 'hidden sm:table-column sm:w-[31%]',
    headerClassName:
      'hidden sm:table-cell px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--anl-text-muted)]',
    cellClassName: 'hidden sm:table-cell px-3 py-3 text-left text-sm text-[var(--anl-text-muted)]',
    header: 'Most Used',
    renderCell: ({ user }) => (
      <span className="block truncate" title={user.most_used_experience || undefined}>
        {user.most_used_experience || '—'}
      </span>
    ),
  },
  {
    key: 'lastSession',
    widthClassName: 'w-[30%] sm:w-[20%]',
    headerClassName:
      'px-2 py-3 sm:px-3 text-center text-xs font-medium uppercase tracking-wider text-[var(--anl-text-muted)]',
    cellClassName: 'px-2 py-3 sm:px-3 text-center',
    header: 'Last Session',
    renderCell: ({ user }) => <LastSessionCell user={user} />,
  },
];

export function MemberTable({
  data,
  onUserClick,
  startRank = 1,
  isLumenaAdmin = true,
}: MemberTableProps) {
  return (
    <div className="mb-4 overflow-x-auto rounded-lg border border-[var(--anl-border)]">
      <table className="w-full table-fixed divide-y divide-[var(--anl-border)]">
        <colgroup>
          {COLUMNS.map(column => (
            <col key={column.key} className={column.widthClassName} />
          ))}
        </colgroup>
        <thead className="bg-[var(--anl-surface-elev)]">
          <tr>
            {COLUMNS.map(column => (
              <th key={column.key} className={column.headerClassName}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--anl-border)] bg-[var(--anl-surface)]">
          {data.map((user, index) => {
            const globalRank = startRank + index;
            return (
              <tr
                key={user.user_id}
                className={`transition-colors ${isLumenaAdmin && onUserClick ? 'cursor-pointer hover:bg-[var(--anl-surface-elev)]' : ''}`}
                onClick={() => isLumenaAdmin && onUserClick?.(user)}
              >
                {COLUMNS.map(column => (
                  <td key={column.key} className={column.cellClassName}>
                    {column.renderCell({ user, globalRank, isLumenaAdmin })}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
